/** @forge-header
 * @id          themeContext
 * @role        Theme context provider | dark/light toggle with system preference fallback
 * @pattern     context-provider
 * @tier        T1
 * @purity      localStorage | dom
 * @units       n/a
 * @upstream    lib/themeManager:initTheme,setTheme,toggleTheme | lib/storage:storageKey
 * @downstream  App.tsx | any component via useTheme()
 * @exports     ThemeProvider:component | useTheme:fn
 * @test        untested
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  Theme,
  initTheme,
  setTheme as setThemeStorage,
  toggleTheme as toggleThemeStorage,
} from '@/lib/infra/themeManager';
import { storageKey } from '@/lib/infra/storage';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => initTheme());

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if user hasn't explicitly set a preference
      const saved = localStorage.getItem(storageKey('app_theme'));
      if (!saved) {
        const newTheme = e.matches ? 'dark' : 'light';
        setThemeStorage(newTheme);
        setThemeState(newTheme);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeStorage(newTheme);
    setThemeState(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    const newTheme = toggleThemeStorage();
    setThemeState(newTheme);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
